import classNames from "classnames";
import { isValid } from "date-fns";
import formatDate from "date-fns/format";
import formatISO from "date-fns/formatISO";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { DayPicker, useInput } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { mergeRefs } from "react-merge-refs";
import { Card } from "../card/Card";
import classes from "./DatePicker.module.scss";

export const DatePicker = React.memo(
  React.forwardRef(({ label, name, defaultValue, error, ...props }, ref) => {
    const [calendarVisibility, setCalendarVisibility] = useState(false);
    const format = props.format || "dd/M/yyyy";
    const { inputProps, dayPickerProps, setSelected, reset } = useInput({
      format,
    });

    useEffect(() => {
      if (defaultValue || props.value) {
        if (isValid(new Date(props.value)))
          setSelected(new Date(props.value || defaultValue));
      } else {
        reset();
      }
    }, [defaultValue, props.value]); //eslint-disable-line
    const localRef = useRef();
    const inputRef = mergeRefs([ref, localRef]);
    const componentRef = useRef();

    const handleInputChange = (e) => {
      if (props.onChange) {
        if (e.target.value && isValid(new Date(e.target.value)))
          props.onChange({
            target: { value: formatISO(e.target.value), name },
          });
      }
    };

    const toggleDatePickerVisibility = () => {
      localRef.current?.focus();
    };

    const handleSelectedDay = (date) => {
      if (date && props.onChange) {
        props.onChange({ target: { value: date.toISOString(), name } });
      }
    };

    useEffect(() => {
      const handleBlur = (e) => {
        if (!componentRef.current?.contains(e.target)) {
          setCalendarVisibility(false);
        }
      };
      document.addEventListener("mousedown", handleBlur);

      //clearing the event
      return () => {
        document.removeEventListener("mousedown", handleBlur);
      };
    }, []);

    return (
      <div
        className={classNames(classes["label"], "input-wrapper", {
          [classes["error"]]: error,
        })}
        data-testid={"container"}
        ref={componentRef}
      >
        <div className={classes["label-text"]}>{label}</div>
        <input
          type={"text"}
          className={classes["input"] + " input"}
          ref={inputRef}
          {...inputProps}
          placeholder={"DD/MM/YYYY"}
          name={name}
          onFocus={() => setCalendarVisibility(true)}
          {...props}
          onChange={handleInputChange}
          value={
            props.value && isValid(new Date(props.value))
              ? formatDate(new Date(props.value), format)
              : ""
          }
        />
        <div
          role={"button"}
          className={`${classes["cal-button"]} icon-calendar-empty`}
          onClick={toggleDatePickerVisibility}
        />
        {calendarVisibility && (
          <div className={classes["picker-wrapper"]}>
            <Card heightAuto>
              <DayPicker
                {...dayPickerProps}
                mode="single"
                modifiersClassNames={{
                  selected: classes.selected,
                }}
                onSelect={handleSelectedDay}
              />
            </Card>
          </div>
        )}
        {error && <span className="text-red text-sm">{error}</span>}
      </div>
    );
  })
);

DatePicker.propTypes = {
  /**
   * label to be shown above the input
   */
  label: PropTypes.string,
  /**
   * input name (provide it to use in a form)
   */
  name: PropTypes.string,
  //TODO: write unit tests for default value
  /**
   * default string value of the datepicker
   */
  defaultValue: PropTypes.string,
};

DatePicker.defaultProps = {
  label: undefined,
  name: undefined,
};
